import React, { useRef, useState } from "react";
import { MoreVertical, Check, CheckCircle, X } from "lucide-react";
import Button, { ButtonType } from "../../components/Assets/Button";
import useOutsideClick from "../../components/Assets/useOutsideClick";
import { IUserRef } from "../../models/userData.model";
import {
  IRepetitiveTaskManager,
  ITaskManagement,
} from "../../models/taskManagement.model";
import Loading from "../AssetsComponent/loading";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { formatDate } from "../../utils/formatDateUtils";
import {
  updateTaskRequestedEndDate,
  updateAssigneeTask,
  updateStopRepetitiveTask,
} from "../../store/slices/taskmanagementSlice";
import TaskManagerDateExtensionModal from "./TaskManagerDateExtensionModal";
import { getUserId } from "../../utils/auth";

interface TaskManagerTableProps {
  users: IUserRef[];
  showRepetitiveTasks: boolean;
  repetitiveTasks: IRepetitiveTaskManager[];
  selectedTasks: string[];
  openDropdown: string | null;
  updateAssignee: (
    taskId: string,
    assignee: string,
    oldAssignee: string
  ) => void;
  onTaskClick: (task: ITaskManagement | IRepetitiveTaskManager) => void;
  onTaskSelection: (taskId: string, isSelected: boolean) => void;
  onSelectAllTasks: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenCompleteModal: (task: ITaskManagement) => void;
  onUpdateStatus: (
    task: ITaskManagement,
    decision: "approved" | "rejected"
  ) => void;
  onEditTask: (task: ITaskManagement) => void;
  onEditRepetitiveTask: (repeat: IRepetitiveTaskManager) => void;
  onOpenDuplicateModal: (task: ITaskManagement) => void;
  onSetOpenDropdown: (id: string | null) => void;
  onSetSelectedTaskForLogs: (id: string) => void;
  onSetIsLogModalOpen: (open: boolean) => void;
  calculateDaysLeft: (endDate?: Date | string | null) => string;
}

const TaskManagerTable: React.FC<TaskManagerTableProps> = ({
  users,
  showRepetitiveTasks,
  repetitiveTasks,
  openDropdown,
  onTaskClick,
  onOpenCompleteModal,
  onUpdateStatus,
  onEditTask,
  onEditRepetitiveTask,
  onOpenDuplicateModal,
  onSetOpenDropdown,
  onSetSelectedTaskForLogs,
  onSetIsLogModalOpen,
  calculateDaysLeft,
  updateAssignee,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(dropdownRef, () => onSetOpenDropdown(null));
  const dispatch = useDispatch<RootDispatch>();
  const { tasks } = useSelector((state: RootState) => state.TaskManagement);
  const { loading } = useSelector((state: RootState) => state.TaskManagement);
  const { statusFilter } = useSelector(
    (state: RootState) => state.TaskManagement
  );
  const [isExtendDateModalOpen, setIsExtendDateModalOpen] = useState(false);
  const [selectedTaskForExtend, setSelectedTaskForExtend] =
    useState<ITaskManagement | null>(null);
  const userId = getUserId();

  const handleExtendDate = async (requestedEndDate: Date) => {
    if (selectedTaskForExtend?._id) {
      await dispatch(
        updateTaskRequestedEndDate({
          taskId: selectedTaskForExtend._id,
          requestedEndDate,
          status: statusFilter,
        })
      ).unwrap();
      setIsExtendDateModalOpen(false);
      setSelectedTaskForExtend(null);
    }
  };

  const handleStopRepetitiveTask = async (
    taskId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    try {
      await dispatch(updateStopRepetitiveTask({ taskId })).unwrap();
    } catch (error) {
      console.error("Failed to stop repetitive task:", error);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (showRepetitiveTasks) {
    return (
      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deadline (Days Left)
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {repetitiveTasks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-gray-500">
                    No repetitive tasks found.
                  </td>
                </tr>
              ) : (
                repetitiveTasks.map((repeat) => (
                  <tr
                    key={repeat._id}
                    className="hover:bg-gray-50"
                    onClick={() =>
                      onTaskClick({
                        ...repeat,
                        startDate: repeat.startDate ?? null,
                        endDate: repeat.endDate ?? null,
                      })
                    }
                  >
                    <td className="px-6 py-2 whitespace-nowrap font-medium text-gray-900">
                      <span className="cursor-pointer hover:text-blue-600 transition-colors">
                        {repeat.taskName}
                      </span>
                    </td>

                    <td className="px-6 py-2 whitespace-nowrap text-gray-500">
                      {(
                        repeat.assignee as unknown as IUserRef
                      )?.username?.capitalizeWords()}
                    </td>

                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {repeat.status?.capitalizeWords?.() || "-"}
                      </span>
                    </td>

                    <td
                      className={`px-6 py-2.5 whitespace-nowrap ${
                        calculateDaysLeft(repeat.endDate) === "Today"
                          ? "text-red-500 font-semibold"
                          : "text-gray-500"
                      }`}
                    >
                      {formatDate(repeat.endDate ?? null)} (
                      {calculateDaysLeft(repeat.endDate)})
                    </td>
                    <td className="px-6 py-2.5 whitespace-nowrap text-left space-x-2 flex items-center justify-end">
                      {!repeat.isCompleted && (
                        <button
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            handleStopRepetitiveTask(repeat._id!, e);
                          }}
                          className="p-2 !text-sm rounded-full inline-flex items-center justify-center hover:bg-red-100 transition-all"
                          aria-label="Stop repetitive task"
                          title="Stop repetitive task"
                        >
                          <svg
                            className="w-5 h-5 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                            />
                          </svg>
                        </button>
                      )}
                      {
                        <svg
                          onClick={(e: React.MouseEvent<SVGElement>) => {
                            e.stopPropagation();
                            onEditRepetitiveTask(repeat);
                          }}
                          className="cursor-pointer hover:scale-95 duration-300"
                          width="16px"
                          height="16px"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            fill="#1C274C"
                            d="M11.9426 1.25L13.5 1.25C13.9142 1.25 14.25 1.58579 14.25 2C14.25 2.41421 13.9142 2.75 13.5 2.75H12C9.62178 2.75 7.91356 2.75159 6.61358 2.92637C5.33517 3.09825 4.56445 3.42514 3.9948 3.9948C3.42514 4.56445 3.09825 5.33517 2.92637 6.61358C2.75159 7.91356 2.75 9.62178 2.75 12C2.75 14.3782 2.75159 16.0864 2.92637 17.3864C3.09825 18.6648 3.42514 19.4355 3.9948 20.0052C4.56445 20.5749 5.33517 20.9018 6.61358 21.0736C7.91356 21.2484 9.62178 21.25 12 21.25C14.3782 21.25 16.0864 21.2484 17.3864 21.0736C18.6648 20.9018 19.4355 20.5749 20.0052 20.0052C20.5749 19.4355 20.9018 18.6648 21.0736 17.3864C21.2484 16.0864 21.25 14.3782 21.25 12V10.5C21.25 10.0858 21.5858 9.75 22 9.75C22.4142 9.75 22.75 10.0858 22.75 10.5V12.0574C22.75 14.3658 22.75 16.1748 22.5603 17.5863C22.366 19.031 21.9607 20.1711 21.0659 21.0659C20.1711 21.9607 19.031 22.366 17.5863 22.5603C16.1748 22.75 14.3658 22.75 12.0574 22.75H11.9426C9.63423 22.75 7.82519 22.75 6.41371 22.5603C4.96897 22.366 3.82895 21.9607 2.93414 21.0659C2.03933 20.1711 1.63399 19.031 1.43975 17.5863C1.24998 16.1748 1.24999 14.3658 1.25 12.0574V11.9426C1.24999 9.63423 1.24998 7.82519 1.43975 6.41371C1.63399 4.96897 2.03933 3.82895 2.93414 2.93414C3.82895 2.03933 4.96897 1.63399 6.41371 1.43975C7.82519 1.24998 9.63423 1.24999 11.9426 1.25ZM16.7705 2.27592C18.1384 0.908029 20.3562 0.908029 21.7241 2.27592C23.092 3.6438 23.092 5.86158 21.7241 7.22947L15.076 13.8776C14.7047 14.2489 14.4721 14.4815 14.2126 14.684C13.9069 14.9224 13.5761 15.1268 13.2261 15.2936C12.929 15.4352 12.6169 15.5392 12.1188 15.7052L9.21426 16.6734C8.67801 16.8521 8.0868 16.7126 7.68711 16.3129C7.28742 15.9132 7.14785 15.322 7.3266 14.7857L8.29477 11.8812C8.46079 11.3831 8.56479 11.071 8.7064 10.7739C8.87319 10.4239 9.07761 10.0931 9.31605 9.78742C9.51849 9.52787 9.7511 9.29529 10.1224 8.924L16.7705 2.27592ZM20.6634 3.33658C19.8813 2.55448 18.6133 2.55448 17.8312 3.33658L17.4546 3.7132C17.4773 3.80906 17.509 3.92327 17.5532 4.05066C17.6965 4.46372 17.9677 5.00771 18.48 5.51999C18.9923 6.03227 19.5363 6.30346 19.9493 6.44677C20.0767 6.49097 20.1909 6.52273 20.2868 6.54543L20.6634 6.16881C21.4455 5.38671 21.4455 4.11867 20.6634 3.33658ZM19.1051 7.72709C18.5892 7.50519 17.9882 7.14946 17.4193 6.58065C16.8505 6.01185 16.4948 5.41082 16.2729 4.89486L11.2175 9.95026C10.801 10.3668 10.6376 10.532 10.4988 10.7099C10.3274 10.9297 10.1804 11.1676 10.0605 11.4192C9.96337 11.623 9.88868 11.8429 9.7024 12.4017L9.27051 13.6974L10.3026 14.7295L11.5983 14.2976C12.1571 14.1113 12.377 14.0366 12.5808 13.9395C12.8324 13.8196 13.0703 13.6726 13.2901 13.5012C13.468 13.3624 13.6332 13.199 14.0497 12.7825L19.1051 7.72709Z"
                          />
                        </svg>
                      }
                      <div className=" text-left">
                        <button
                          title="Options"
                          className="inline-flex items-center justify-center p-2 rounded-full hover:bg-gray-200 transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSetOpenDropdown(
                              openDropdown === repeat._id ? null : repeat._id!
                            );
                          }}
                        >
                          <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>

                        {openDropdown === repeat._id && (
                          <div
                            ref={dropdownRef}
                            className="absolute right-0 mt-2 w-40 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg z-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                onSetSelectedTaskForLogs(repeat._id!);
                                onSetIsLogModalOpen(true);
                                onSetOpenDropdown(null);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              View Logs
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                {/* {
                  <input
                    type="checkbox"
                    checked={
                      selectedTasks.length === tasks.length && tasks.length > 0
                    }
                    onChange={onSelectAllTasks}
                  />
                } */}
                Task Name
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Assignee
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Author
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>

            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Deadline (Days Left)
            </th>

            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Completion Date
            </th>

            <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 text-xs">
          {tasks.map((task) => (
            <tr
              key={task._id}
              className={`transition-colors 
    ${
      task.status === "overdue"
        ? "bg-red-light hover:bg-red-100"
        : "hover:bg-gray-50"
    }`}
              onClick={() => onTaskClick(task)}
            >
              <td className="px-6 py-2.5 whitespace-nowrap font-medium text-gray-900 relative">
                <div className="flex items-center gap-2">
                  {/* {
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task._id!)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onTaskSelection(task._id!, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  } */}
                  {task.repetitiveTaskId && (
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  )}
                  <span className="cursor-pointer hover:text-blue-600 transition-colors">
                    {task.taskName.capitalizeWords()}
                  </span>
                </div>
              </td>
              <td className="px-6 py-2.5 whitespace-nowrap text-gray-500">
                {["waiting for approval", "completed", "duplicate"].includes(
                  task.status?.toLowerCase()
                ) ? (
                  (
                    task.assignee as unknown as IUserRef
                  )?.username?.capitalizeWords()
                ) : getUserId() ===
                    (task.assignee as unknown as IUserRef)?._id ||
                  getUserId() === (task.author as unknown as IUserRef)?._id ? (
                  <AssigneeSelector
                    taskId={task._id!}
                    users={users}
                    assignee={(task.assignee as unknown as IUserRef)?._id || ""}
                    onAssigneeChange={(val) => {
                      updateAssignee(
                        task._id!,
                        val,
                        (task.assignee as unknown as IUserRef)?._id
                      );
                      console.log("Change Assignee to:", val);
                    }}
                  />
                ) : (
                  (
                    task.assignee as unknown as IUserRef
                  )?.username?.capitalizeWords()
                )}
              </td>
              <td className="px-6 py-2.5 whitespace-nowrap text-gray-500">
                {(
                  task.author as unknown as IUserRef
                )?.username?.capitalizeWords()}
              </td>
              <td className="px-6 py-2.5 whitespace-nowrap">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                  {task.status.capitalizeWords()}
                </span>
              </td>
              <td
                className={`px-6 py-2.5 whitespace-nowrap ${
                  calculateDaysLeft(task.endDate) === "Today"
                    ? "text-red-500 font-semibold"
                    : "text-gray-500"
                }`}
              >
                {task.status === "ongoing" ||
                task.status === "waiting for approval" ||
                task.status === "overdue"
                  ? `${formatDate(task.endDate)} (${calculateDaysLeft(
                      task.endDate
                    )})`
                  : "-"}
              </td>
              <td className="px-6 py-2.5 whitespace-nowrap text-gray-500">
                {task.status === "completed"
                  ? formatDate(task.completionDate ?? null)
                  : "-"}
              </td>
              <td className="px-6 py-2.5 whitespace-nowrap text-left space-x-2 flex items-center justify-end">
                {(task.status === "ongoing" || task.status === "overdue") &&
                  (getUserId() ===
                    (task.assignee as unknown as IUserRef)?._id ||
                    getUserId() ===
                      (task.author as unknown as IUserRef)?._id) && (
                    <button
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onOpenCompleteModal(task);
                      }}
                      className="p-2 !text-sm rounded-full inline-flex items-center justify-center hover:bg-green-100 transition-all"
                      aria-label="Mark complete"
                      title="Complete"
                    >
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </button>
                  )}
                {task.status === "waiting for approval" &&
                  (task.author as unknown as IUserRef)?._id === userId && (
                    <div className="flex items-center gap-2">
                      <Button
                        type={ButtonType.Extra}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          onUpdateStatus(task, "approved");
                        }}
                        aria-label="Approved"
                        className="flex items-center gap-1"
                      >
                        <Check className="w-4 h-4 text-white" />
                        <span>Approve</span>
                      </Button>
                      <Button
                        type={ButtonType.Error}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          onUpdateStatus(task, "rejected");
                        }}
                        aria-label="Rejected"
                        className="flex items-center gap-1"
                      >
                        <X className="w-4 h-4" />

                        <span>Reject</span>
                      </Button>
                    </div>
                  )}

                {(task.status === "ongoing" || task.status === "overdue") &&
                  (task.author as unknown as IUserRef)?._id === getUserId() && (
                    <svg
                      onClick={(e: React.MouseEvent<SVGElement>) => {
                        e.stopPropagation();
                        onEditTask(task);
                      }}
                      className="cursor-pointer hover:scale-95 duration-300"
                      width="16px"
                      height="16px"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        fill="#1C274C"
                        d="M11.9426 1.25L13.5 1.25C13.9142 1.25 14.25 1.58579 14.25 2C14.25 2.41421 13.9142 2.75 13.5 2.75H12C9.62178 2.75 7.91356 2.75159 6.61358 2.92637C5.33517 3.09825 4.56445 3.42514 3.9948 3.9948C3.42514 4.56445 3.09825 5.33517 2.92637 6.61358C2.75159 7.91356 2.75 9.62178 2.75 12C2.75 14.3782 2.75159 16.0864 2.92637 17.3864C3.09825 18.6648 3.42514 19.4355 3.9948 20.0052C4.56445 20.5749 5.33517 20.9018 6.61358 21.0736C7.91356 21.2484 9.62178 21.25 12 21.25C14.3782 21.25 16.0864 21.2484 17.3864 21.0736C18.6648 20.9018 19.4355 20.5749 20.0052 20.0052C20.5749 19.4355 20.9018 18.6648 21.0736 17.3864C21.2484 16.0864 21.25 14.3782 21.25 12V10.5C21.25 10.0858 21.5858 9.75 22 9.75C22.4142 9.75 22.75 10.0858 22.75 10.5V12.0574C22.75 14.3658 22.75 16.1748 22.5603 17.5863C22.366 19.031 21.9607 20.1711 21.0659 21.0659C20.1711 21.9607 19.031 22.366 17.5863 22.5603C16.1748 22.75 14.3658 22.75 12.0574 22.75H11.9426C9.63423 22.75 7.82519 22.75 6.41371 22.5603C4.96897 22.366 3.82895 21.9607 2.93414 21.0659C2.03933 20.1711 1.63399 19.031 1.43975 17.5863C1.24998 16.1748 1.24999 14.3658 1.25 12.0574V11.9426C1.24999 9.63423 1.24998 7.82519 1.43975 6.41371C1.63399 4.96897 2.03933 3.82895 2.93414 2.93414C3.82895 2.03933 4.96897 1.63399 6.41371 1.43975C7.82519 1.24998 9.63423 1.24999 11.9426 1.25ZM16.7705 2.27592C18.1384 0.908029 20.3562 0.908029 21.7241 2.27592C23.092 3.6438 23.092 5.86158 21.7241 7.22947L15.076 13.8776C14.7047 14.2489 14.4721 14.4815 14.2126 14.684C13.9069 14.9224 13.5761 15.1268 13.2261 15.2936C12.929 15.4352 12.6169 15.5392 12.1188 15.7052L9.21426 16.6734C8.67801 16.8521 8.0868 16.7126 7.68711 16.3129C7.28742 15.9132 7.14785 15.322 7.3266 14.7857L8.29477 11.8812C8.46079 11.3831 8.56479 11.071 8.7064 10.7739C8.87319 10.4239 9.07761 10.0931 9.31605 9.78742C9.51849 9.52787 9.7511 9.29529 10.1224 8.924L16.7705 2.27592ZM20.6634 3.33658C19.8813 2.55448 18.6133 2.55448 17.8312 3.33658L17.4546 3.7132C17.4773 3.80906 17.509 3.92327 17.5532 4.05066C17.6965 4.46372 17.9677 5.00771 18.48 5.51999C18.9923 6.03227 19.5363 6.30346 19.9493 6.44677C20.0767 6.49097 20.1909 6.52273 20.2868 6.54543L20.6634 6.16881C21.4455 5.38671 21.4455 4.11867 20.6634 3.33658ZM19.1051 7.72709C18.5892 7.50519 17.9882 7.14946 17.4193 6.58065C16.8505 6.01185 16.4948 5.41082 16.2729 4.89486L11.2175 9.95026C10.801 10.3668 10.6376 10.532 10.4988 10.7099C10.3274 10.9297 10.1804 11.1676 10.0605 11.4192C9.96337 11.623 9.88868 11.8429 9.7024 12.4017L9.27051 13.6974L10.3026 14.7295L11.5983 14.2976C12.1571 14.1113 12.377 14.0366 12.5808 13.9395C12.8324 13.8196 13.0703 13.6726 13.2901 13.5012C13.468 13.3624 13.6332 13.199 14.0497 12.7825L19.1051 7.72709Z"
                      />
                    </svg>
                  )}
                <div className=" text-left">
                  <button
                    title="Options"
                    className="inline-flex items-center justify-center p-2 rounded-full hover:bg-gray-200 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetOpenDropdown(
                        openDropdown === task._id ? null : task._id!
                      );
                    }}
                  >
                    <MoreVertical className="w-5 h-5 text-gray-600" />
                  </button>

                  {openDropdown === task._id && (
                    <div
                      ref={dropdownRef}
                      className="absolute right-0 mt-2 w-40 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {task.status === "ongoing" &&
                        getUserId() ===
                          (task.assignee as unknown as IUserRef)?._id && (
                          <button
                            onClick={() => {
                              onOpenDuplicateModal(task);
                              onSetOpenDropdown(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Duplicate
                          </button>
                        )}
                      <button
                        onClick={() => {
                          onSetSelectedTaskForLogs(task._id!);
                          onSetIsLogModalOpen(true);
                          onSetOpenDropdown(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        View Logs
                      </button>
                      {getUserId() ===
                        (task.assignee as unknown as IUserRef)?._id &&
                        (task.status === "ongoing" ||
                          task.status === "overdue") && (
                          <button
                            onClick={() => {
                              setSelectedTaskForExtend(task);
                              setIsExtendDateModalOpen(true);
                              onSetOpenDropdown(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Extend Date
                          </button>
                        )}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isExtendDateModalOpen && (
        <TaskManagerDateExtensionModal
          isOpen={isExtendDateModalOpen}
          onClose={() => {
            setIsExtendDateModalOpen(false);
            setSelectedTaskForExtend(null);
          }}
          onSubmit={handleExtendDate}
          currentEndDate={selectedTaskForExtend?.endDate}
        />
      )}
    </div>
  );
};

export default TaskManagerTable;

interface AssigneeSelectorProps {
  taskId: string;
  users: IUserRef[];
  assignee: string;
  onAssigneeChange: (value: string) => void;
}

const AssigneeSelector: React.FC<AssigneeSelectorProps> = ({
  taskId,
  users,
  assignee,
  onAssigneeChange,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  return (
    <div onClick={(e) => e.stopPropagation()} className="flex flex-col">
      <select
        id="assignee-filter"
        value={assignee}
        onChange={(e) => {
          const selectAssignee = users.find(
            (user) => user._id === e.target.value
          );
          dispatch(updateAssigneeTask({ taskId, assignee: selectAssignee }));
          onAssigneeChange(e.target.value);
        }}
        className="w-auto border border-gray-300 rounded-lg px-3 py-2 text-xs
                   focus:outline-none focus:ring-2 focus:ring-blue-500 
                   hover:border-blue-400 transition"
      >
        {/* Default option */}
        <option value="">All Assignees</option>

        {/* Dynamic user list */}
        {users.map((user) => (
          <option key={user._id} value={user._id}>
            {user.username.capitalizeWords()}
          </option>
        ))}
      </select>
    </div>
  );
};
